import React, { useEffect } from 'react';

import { Button, Divider, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import AthleteStatusSelect from 'components/form-fields/select-fields/AthleteStatusSelect';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import useCreateAthleteStatus from 'hooks/athleteStatus/useCreateAthleteStatus';
import { AthleteRetrieve, AthleteStatus } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  status: AthleteStatus['status'] | null;
  startDate: Date;
}

interface ICreateStatusModalProps {
  opened: boolean;
  athlete: AthleteRetrieve;
  previousStatus: AthleteStatus | null;
  closeHandler: () => void;
}

const CreateStatusModal = ({
  opened,
  athlete,
  previousStatus,
  closeHandler,
}: ICreateStatusModalProps): JSX.Element => {
  const today = new Date();
  const previousStatusStartDate = previousStatus ? new Date(previousStatus.startDate) : null;

  const createMutation = useCreateAthleteStatus();
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      status: null,
      startDate: today,
    },
    validate: {
      status: value => {
        if (!value) {
          return 'Please select a valid status';
        }
        if (value === previousStatus?.status) {
          return 'Please select a status different than the current status';
        }
        return null;
      },
      startDate: value => {
        if (!value) return 'Please select a valid date';
        if (previousStatusStartDate && value < previousStatusStartDate) return 'Please select a later date';
        if (value > today) return 'Please select an earlier date';
        return null;
      },
    },
  });

  const handleCloseAndReset = (): void => {
    form.reset();
    closeHandler();
  };

  const handleDateChange = (value: Date): void => {
    const newValue =
      previousStatusStartDate && value < previousStatusStartDate ? previousStatusStartDate : value;
    form.setFieldValue('startDate', newValue);
  };

  const handleSubmit = (values: FormValues): void => {
    if (values.status) {
      createMutation.mutate(
        {
          athleteStatusId: uuidv4(),
          athlete: athlete.athleteId,
          startDate: values.startDate.toISOString(),
          status: values.status,
        },
        {
          onSuccess: () => {
            successNotification('Athlete status updated', 'Athlete status has been updated successfully');
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification('Athlete status update failed', error.response?.data.message);
          },
        },
      );
    }
  };

  useEffect(() => {
    form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousStatus]);

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      size='md'
      centered
      title={<Title order={4}>Change Status</Title>}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <Stack spacing='xs'>
            <Group noWrap>
              <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
                Athlete Name
              </Text>
              <Text fz='sm' ta='left'>
                {athlete.fullName}
              </Text>
            </Group>
            <Group noWrap>
              <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
                Current Status
              </Text>
              <Text fz='sm' ta='left'>
                <AthleteStatusBadge status={athlete.athleteStatus} />
              </Text>
            </Group>
          </Stack>

          <Divider mx='-xl' />

          <Text color='dimmed' size='sm'>
            Changing this status will end the current status and add the new status effective on the selected
            date.
          </Text>
          <AthleteStatusSelect
            {...form.getInputProps('status')}
            data-cy='selectStatus'
            label='New Status'
            placeholder='Select a status'
            required
            zIndex={9999}
          />
          <DatePickerInput
            {...form.getInputProps('startDate')}
            label='Start Date'
            required
            minDate={previousStatusStartDate || undefined}
            maxDate={today}
            onChange={handleDateChange}
            dropdownType='modal'
            modalProps={{ centered: true }}
          />
        </Stack>

        <Group position='right' pt='md'>
          <Button variant='light' onClick={handleCloseAndReset} size='sm' radius='md' disabled={false}>
            Cancel
          </Button>
          <Button
            type='submit'
            size='sm'
            radius='md'
            loading={createMutation.isLoading}
            data-cy='saveStatusButton'
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default CreateStatusModal;
